import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Alert,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import axios from 'axios';
import CreateEvidence from './CreateEvidence';
import EvidenceDetail from './EvidenceDetail';

const EvidenceLibrary = ({ sspId = null }) => {
  const [evidence, setEvidence] = useState([]);
  const [filteredEvidence, setFilteredEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterSensitivity, setFilterSensitivity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    fetchEvidence();
  }, [sspId]);

  useEffect(() => {
    applyFilters();
  }, [evidence, filterType, filterSensitivity, searchTerm]);

  const fetchEvidence = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (sspId) params.sspId = sspId;

      const response = await axios.get(`${baseURL}/ssp-studio/evidence`, {
        params,
        withCredentials: true
      });

      if (response.data.success) {
        setEvidence(response.data.data);
      } else {
        setError('Failed to load evidence');
      }
    } catch (err) {
      console.error('Error fetching evidence:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...evidence];

    // Filter by type
    if (filterType) {
      filtered = filtered.filter(e => e.type === filterType);
    }

    // Filter by sensitivity
    if (filterSensitivity) {
      filtered = filtered.filter(e => e.sensitivity === filterSensitivity);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        e.name.toLowerCase().includes(term) ||
        (e.notes && e.notes.toLowerCase().includes(term)) ||
        (e.linkedControls && e.linkedControls.some(c => c.toLowerCase().includes(term)))
      );
    }

    setFilteredEvidence(filtered);
  };

  const handleDelete = async (evidenceId) => {
    if (!window.confirm('Are you sure you want to delete this evidence?')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${baseURL}/ssp-studio/evidence/${evidenceId}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        fetchEvidence();
      } else {
        setError(response.data.message || 'Failed to delete evidence');
      }
    } catch (err) {
      console.error('Error deleting evidence:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleView = (item) => {
    setSelectedEvidence(item);
    setShowCreate(false);
  };

  const handleCreateClick = () => {
    setShowCreate(true);
    setSelectedEvidence(null);
  };

  const handleBack = () => {
    setShowCreate(false);
    setSelectedEvidence(null);
    fetchEvidence();
  };

  const getTypeColor = (type) => {
    const colors = {
      scan: 'info',
      policy: 'primary',
      procedure: 'success',
      screenshot: 'warning',
      config: 'secondary',
      ticket: 'danger',
      other: 'dark'
    };
    return colors[type] || 'secondary';
  };

  const getSensitivityColor = (sensitivity) => {
    const colors = {
      public: 'success',
      internal: 'info',
      cui: 'warning',
      sensitive: 'danger'
    };
    return colors[sensitivity] || 'secondary';
  };

  const getTypeIcon = (type) => {
    const icons = {
      scan: 'mdi-radar',
      policy: 'mdi-file-document',
      procedure: 'mdi-clipboard-list',
      screenshot: 'mdi-image',
      config: 'mdi-cog',
      ticket: 'mdi-ticket',
      other: 'mdi-file'
    };
    return icons[type] || 'mdi-file';
  };

  const isExpiringSoon = (evidence) => {
    if (!evidence.lastUpdated || !evidence.cadenceDays) return false;
    const daysSinceUpdate = Math.floor((Date.now() - new Date(evidence.lastUpdated)) / (1000 * 60 * 60 * 24));
    return daysSinceUpdate >= evidence.cadenceDays - 7; // Within 7 days of expiry
  };

  // If viewing/editing a specific evidence item
  if (selectedEvidence) {
    return (
      <EvidenceDetail
        evidence={selectedEvidence}
        onBack={handleBack}
      />
    );
  }

  // If creating new evidence
  if (showCreate) {
    return (
      <CreateEvidence
        sspId={sspId}
        onBack={handleBack}
        onCreated={handleBack}
      />
    );
  }

  // Main evidence library view
  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading evidence library...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h4>
            <i className="mdi mdi-folder-multiple-outline me-2"></i>
            Evidence Library
          </h4>
          <p className="text-muted">
            Manage evidence artifacts for security controls and SSP sections
          </p>
        </Col>
        <Col xs="auto">
          <Button color="success" onClick={handleCreateClick}>
            <i className="mdi mdi-plus me-1"></i>
            Add Evidence
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="searchTerm" className="small text-muted">Search</Label>
                <Input
                  type="text"
                  id="searchTerm"
                  placeholder="Search by name, notes, or controls..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="filterType" className="small text-muted">Type</Label>
                <Input
                  type="select"
                  id="filterType"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="scan">Scan</option>
                  <option value="policy">Policy</option>
                  <option value="procedure">Procedure</option>
                  <option value="screenshot">Screenshot</option>
                  <option value="config">Configuration</option>
                  <option value="ticket">Ticket</option>
                  <option value="other">Other</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="filterSensitivity" className="small text-muted">Sensitivity</Label>
                <Input
                  type="select"
                  id="filterSensitivity"
                  value={filterSensitivity}
                  onChange={(e) => setFilterSensitivity(e.target.value)}
                >
                  <option value="">All Levels</option>
                  <option value="public">Public</option>
                  <option value="internal">Internal</option>
                  <option value="cui">CUI</option>
                  <option value="sensitive">Sensitive</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                color="secondary"
                outline
                size="sm"
                block
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('');
                  setFilterSensitivity('');
                }}
              >
                <i className="mdi mdi-filter-off me-1"></i>
                Clear
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Evidence List */}
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <strong>Evidence Items</strong>
              <Badge color="light" className="ms-2 text-dark">
                {filteredEvidence.length} {filteredEvidence.length === evidence.length ? 'total' : `of ${evidence.length}`}
              </Badge>
            </span>
          </div>
        </CardHeader>
        <CardBody>
          {filteredEvidence.length === 0 ? (
            <div className="text-center py-5">
              <i className="mdi mdi-folder-open-outline display-4 text-muted"></i>
              <h5 className="mt-3">No Evidence Found</h5>
              <p className="text-muted">
                {evidence.length === 0
                  ? 'Add your first evidence artifact to get started'
                  : 'No evidence matches the current filters'}
              </p>
              {evidence.length === 0 && (
                <Button color="primary" onClick={handleCreateClick}>
                  <i className="mdi mdi-plus me-1"></i>
                  Add Evidence
                </Button>
              )}
            </div>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th style={{ width: '5%' }}></th>
                  <th style={{ width: '30%' }}>Name</th>
                  <th style={{ width: '10%' }}>Type</th>
                  <th style={{ width: '15%' }}>Linked Controls</th>
                  <th style={{ width: '10%' }}>Sensitivity</th>
                  <th style={{ width: '12%' }}>Last Updated</th>
                  <th style={{ width: '10%' }}>Status</th>
                  <th style={{ width: '8%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvidence.map((item) => {
                  const expiringSoon = isExpiringSoon(item);
                  const daysSinceUpdate = Math.floor(
                    (Date.now() - new Date(item.lastUpdated)) / (1000 * 60 * 60 * 24)
                  );

                  return (
                    <tr key={item._id}>
                      <td>
                        <i className={`mdi ${getTypeIcon(item.type)} text-${getTypeColor(item.type)}`}></i>
                      </td>
                      <td>
                        <div>
                          <strong>{item.name}</strong>
                          {item.url && (
                            <div className="small text-muted">
                              <i className="mdi mdi-link-variant me-1"></i>
                              <a href={item.url} target="_blank" rel="noopener noreferrer">
                                {item.url.substring(0, 50)}{item.url.length > 50 ? '...' : ''}
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge color={getTypeColor(item.type)}>
                          {item.type}
                        </Badge>
                      </td>
                      <td>
                        {item.linkedControls && item.linkedControls.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {item.linkedControls.slice(0, 3).map((ctrl, idx) => (
                              <Badge key={idx} color="secondary" className="small">
                                {ctrl}
                              </Badge>
                            ))}
                            {item.linkedControls.length > 3 && (
                              <Badge color="light" className="text-dark small">
                                +{item.linkedControls.length - 3}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <Badge color={getSensitivityColor(item.sensitivity)}>
                          {item.sensitivity}
                        </Badge>
                      </td>
                      <td>
                        <small>
                          {new Date(item.lastUpdated).toLocaleDateString()}
                          <div className="text-muted">
                            {daysSinceUpdate} days ago
                          </div>
                        </small>
                      </td>
                      <td>
                        {expiringSoon ? (
                          <Badge color="warning">
                            <i className="mdi mdi-alert me-1"></i>
                            Expiring
                          </Badge>
                        ) : (
                          <Badge color="success">Current</Badge>
                        )}
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle color="link" size="sm" className="p-0">
                            <i className="mdi mdi-dots-vertical"></i>
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem onClick={() => handleView(item)}>
                              <i className="mdi mdi-eye me-2"></i>
                              View/Edit
                            </DropdownItem>
                            <DropdownItem divider />
                            <DropdownItem onClick={() => handleDelete(item._id)} className="text-danger">
                              <i className="mdi mdi-delete me-2"></i>
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default EvidenceLibrary;
